import { useState } from "react";
import { useLang } from "../../context/LanguageContext";
import { useSite } from "../../context/SiteContext";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Phone, Mail, MessageCircle } from "lucide-react";
import { trackContactSubmit, trackWhatsappClick, trackPhoneClick } from "../../lib/analytics";

export default function Contact() {
  const { lang, t } = useLang();
  const { site } = useSite();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    check_in: "",
    check_out: "",
    guests: 2,
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error(lang === "el" ? "Συμπληρώστε τα υποχρεωτικά πεδία" : "Please fill required fields");
      return;
    }
    setLoading(true);
    try {
      await api.post("/contact", form);
      trackContactSubmit();
      toast.success(t.contact.sent);
      setForm({ name: "", email: "", phone: "", check_in: "", check_out: "", guests: 2, message: "" });
    } catch (e) {
      toast.error("Error");
    } finally {
      setLoading(false);
    }
  };

  const waMsg = encodeURIComponent(
    lang === "el"
      ? "Γειά σας! Ενδιαφέρομαι για κράτηση στο Sithonia Holidays."
      : "Hello! I'm interested in booking at Sithonia Holidays."
  );

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-16 sm:py-24 lg:py-32 bg-[#F5F2EB] overflow-x-hidden"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mb-10 sm:mb-16">
          <p className="uppercase text-xs sm:text-sm tracking-[0.25em] text-[#6B705C] mb-3 sm:mb-4">
            {t.contact.overline}
          </p>
          <h2 className="font-serif text-3xl sm:text-5xl lg:text-6xl text-[#1A2B4C] tracking-tight leading-[1.05] mb-3 sm:mb-4">
            {t.contact.title}
          </h2>
          <p className="text-gray-600 text-base sm:text-lg">{t.contact.subtitle}</p>
        </div>

        <div className="grid lg:grid-cols-12 gap-6 lg:gap-10">
          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="lg:col-span-7 bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-10 border border-[#E9E2D3] space-y-4 sm:space-y-5"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <input
                placeholder={t.contact.name + " *"}
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="contact-name"
                className="bg-[#F5F2EB] border border-[#E9E2D3] rounded-xl px-4 py-3.5 text-[#1A2B4C] focus:outline-none focus:ring-2 focus:ring-[#1A2B4C]/30"
              />
              <input
                type="email"
                placeholder={t.contact.email + " *"}
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="contact-email"
                className="bg-[#F5F2EB] border border-[#E9E2D3] rounded-xl px-4 py-3.5 text-[#1A2B4C] focus:outline-none focus:ring-2 focus:ring-[#1A2B4C]/30"
              />
            </div>
            <input
              placeholder={t.contact.phone}
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              data-testid="contact-phone"
              className="w-full bg-[#F5F2EB] border border-[#E9E2D3] rounded-xl px-4 py-3.5 text-[#1A2B4C] focus:outline-none focus:ring-2 focus:ring-[#1A2B4C]/30"
            />
            <div className="grid sm:grid-cols-3 gap-5">
              <div>
                <label className="text-xs uppercase tracking-widest text-[#6B705C] mb-1.5 block">
                  {t.contact.checkin}
                </label>
                <input
                  type="date"
                  value={form.check_in}
                  onChange={(e) => setForm({ ...form, check_in: e.target.value })}
                  className="w-full bg-[#F5F2EB] border border-[#E9E2D3] rounded-xl px-4 py-3 text-[#1A2B4C] focus:outline-none"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-[#6B705C] mb-1.5 block">
                  {t.contact.checkout}
                </label>
                <input
                  type="date"
                  value={form.check_out}
                  onChange={(e) => setForm({ ...form, check_out: e.target.value })}
                  className="w-full bg-[#F5F2EB] border border-[#E9E2D3] rounded-xl px-4 py-3 text-[#1A2B4C] focus:outline-none"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-[#6B705C] mb-1.5 block">
                  {t.contact.guests}
                </label>
                <input
                  type="number"
                  min="1"
                  value={form.guests}
                  onChange={(e) => setForm({ ...form, guests: parseInt(e.target.value) || 1 })}
                  className="w-full bg-[#F5F2EB] border border-[#E9E2D3] rounded-xl px-4 py-3 text-[#1A2B4C] focus:outline-none"
                />
              </div>
            </div>
            <textarea
              rows="4"
              placeholder={t.contact.message + " *"}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              data-testid="contact-message"
              className="w-full bg-[#F5F2EB] border border-[#E9E2D3] rounded-xl px-4 py-3.5 text-[#1A2B4C] focus:outline-none focus:ring-2 focus:ring-[#1A2B4C]/30"
            />
            <button
              type="submit"
              disabled={loading}
              data-testid="contact-submit"
              className="w-full bg-[#1A2B4C] text-white py-4 rounded-full text-sm tracking-widest uppercase hover:-translate-y-0.5 transition-transform disabled:opacity-50"
            >
              {loading ? "..." : t.contact.send}
            </button>
          </form>

          <div className="lg:col-span-5 space-y-4">
            <a
              href={`https://wa.me/${site.whatsapp}?text=${waMsg}`}
              target="_blank"
              rel="noreferrer"
              data-testid="contact-whatsapp"
              onClick={trackWhatsappClick}
              className="flex items-center gap-4 bg-white rounded-2xl p-6 border border-[#E9E2D3] hover:shadow-md hover:-translate-y-0.5 transition-all"
            >
              <div className="w-12 h-12 rounded-full bg-[#25D366]/10 flex items-center justify-center">
                <MessageCircle className="w-5 h-5 text-[#25D366]" />
              </div>
              <div>
                <p className="font-serif text-xl text-[#1A2B4C]">{t.contact.whatsapp}</p>
                <p className="text-sm text-[#6B705C]">{site.phone_display}</p>
              </div>
            </a>

            <a
              href={`tel:${site.phone}`}
              data-testid="contact-call"
              onClick={trackPhoneClick}
              className="flex items-center gap-4 bg-white rounded-2xl p-6 border border-[#E9E2D3] hover:shadow-md hover:-translate-y-0.5 transition-all"
            >
              <div className="w-12 h-12 rounded-full bg-[#1A2B4C]/10 flex items-center justify-center">
                <Phone className="w-5 h-5 text-[#1A2B4C]" />
              </div>
              <div>
                <p className="font-serif text-xl text-[#1A2B4C]">{t.contact.call}</p>
                <p className="text-sm text-[#6B705C]">{site.phone_display}</p>
              </div>
            </a>

            <a
              href={`mailto:${site.email}`}
              data-testid="contact-email-link"
              className="flex items-center gap-4 bg-white rounded-2xl p-6 border border-[#E9E2D3] hover:shadow-md hover:-translate-y-0.5 transition-all"
            >
              <div className="w-12 h-12 rounded-full bg-[#6B705C]/10 flex items-center justify-center">
                <Mail className="w-5 h-5 text-[#6B705C]" />
              </div>
              <div>
                <p className="font-serif text-xl text-[#1A2B4C]">{t.contact.emailUs}</p>
                <p className="text-sm text-[#6B705C]">{site.email}</p>
              </div>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
